import type { GameId } from "@bing/shared";
import type { ApiClient } from "../api/client";

/**
 * Sharing a PRIVATE TABLE through Telegram's OWN share sheet.
 *
 * Two steps, both required:
 *  1. `POST /me/table/prepared` mints a *prepared inline message* (Bot API 8.0) —
 *     the server owns the per-game copy, the `?start=t_<game>_<roomId>` deep link
 *     and the no-link-preview flag, so the message is identical wherever it lands.
 *  2. The SDK's `shareMessage(id)` opens Telegram's native chat picker (which
 *     supports selecting SEVERAL chats), and Telegram posts the message itself.
 *
 * Framework-free and I/O-injectable so it is unit-testable, and so the waiting
 * room only has to render the result.
 */

/** What the caller should do next. */
export type TableShareStatus =
  /** Telegram's share sheet handled it — nothing left to do. */
  | "shared"
  /** The host OPENED the sheet and closed it without picking a chat. A choice,
   *  not a fault: the caller must NOT fall back (popping a second, different
   *  share sheet at someone who just cancelled one is obnoxious). */
  | "declined"
  /** This client can't do a native share (old Telegram, or the server can't mint
   *  the card). The caller should fall back to `shareURL` / copy-link. */
  | "unavailable"
  /** It genuinely failed (network, server, Telegram refusal). Same fallback, but
   *  worth telling the user something went wrong. */
  | "failed";

/** The one api method this needs — a narrow port, so tests need no full client.
 *  (The method is optional on `ApiClient`; a client that lacks it is simply
 *  `"unavailable"` — see the note on `ApiClient.createTableShare`.) */
export type TableShareApi = Pick<ApiClient, "createTableShare">;

export interface TableShareDeps {
  /** Hands a prepared-message id to Telegram's share sheet. Defaults to the SDK's
   *  `shareMessage`; injected in tests. */
  share?: (preparedId: string) => Promise<void>;
  log?: (message: string, error: unknown) => void;
}

/**
 * The default sheet driver: the real SDK.
 *
 * Deliberately NOT gated on `shareMessage.isAvailable()`. That check is
 * conservative and returning early on it is exactly how the follow-invite share
 * once failed SILENTLY (see CLAUDE.md / `ShareInvite`): call it, and let the
 * throw tell us what actually happened.
 */
async function shareViaSdk(preparedId: string): Promise<void> {
  const sdk = await import("@telegram-apps/sdk");
  await sdk.shareMessage(preparedId);
}

/** The SDK throws this when the method isn't supported by the running Telegram
 *  client (or the SDK isn't initialized — i.e. we're not inside Telegram). */
function isUnavailableError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  if (name === "FunctionNotAvailableError") return true;
  const message = error instanceof Error ? error.message : "";
  return /not (?:available|supported)|unsupported|not initialized/i.test(message);
}

/**
 * Telegram answers `web_app_send_prepared_message` with `prepared_message_failed`
 * carrying a REASON, which the SDK re-throws as a `ShareMessageError` whose
 * message IS that reason (verified in @telegram-apps/sdk 3.11.8). `USER_DECLINED`
 * = the host closed the chat picker on purpose; every other reason (`UNSUPPORTED`,
 * `MESSAGE_EXPIRED`, `MESSAGE_SEND_FAILED`) is a real problem.
 */
function isDeclined(error: unknown): boolean {
  return error instanceof Error && /user_declined/i.test(error.message);
}

/** `ApiError` carries the HTTP status; a 503 means the server has no card minter. */
function httpStatusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Mint the table invite and open Telegram's share sheet with it.
 *
 * Never throws — the caller switches on the status (`"shared"` → done; anything
 * else → fall back to the copy/`shareURL` affordances).
 */
export async function shareTableInvite(
  api: TableShareApi,
  game: GameId,
  roomId: string,
  deps: TableShareDeps = {},
): Promise<TableShareStatus> {
  const share = deps.share ?? shareViaSdk;
  const mint = api.createTableShare?.bind(api);
  // A client that cannot mint the card at all (see the interface note) — the
  // caller's copy-link / `shareURL` fallback is exactly the right answer.
  if (!mint) return "unavailable";
  let preparedId: string;
  try {
    preparedId = (await mint(game, roomId)).id;
  } catch (error) {
    deps.log?.("[table-share] could not mint the invite", error);
    // 503 = this deployment can't mint share cards at all → a plain fallback,
    // not an error the host should be alarmed by.
    return httpStatusOf(error) === 503 ? "unavailable" : "failed";
  }
  try {
    await share(preparedId);
    return "shared";
  } catch (error) {
    // The host cancelled the picker — the sheet WORKED. Say so, so the caller
    // doesn't "helpfully" open another one.
    if (isDeclined(error)) return "declined";
    deps.log?.("[table-share] the share sheet did not open", error);
    return isUnavailableError(error) ? "unavailable" : "failed";
  }
}
